import { useEffect, useRef, useState, type DragEvent } from 'react'
import {
  applyStoredOrder,
  moveItem,
  parseStoredOrder,
  sameOrder,
  type DropPosition,
} from '@/lib/reorder'

/** Which way the items flow: `x` for a row of tabs, `y` for a column of links. */
export type ReorderAxis = 'x' | 'y'

export interface ReorderItemProps {
  draggable?: boolean
  'data-reorder-id'?: string
  /** Set on the item being dragged, so it can be dimmed. */
  'data-dragging'?: boolean | undefined
  /** Set on the item the drop would land next to: draw the insertion line on this side. */
  'data-drop'?: DropPosition | undefined
  onDragStart?: (e: DragEvent<HTMLElement>) => void
  onDragEnd?: () => void
}

export interface ReorderContainerProps {
  onDragOver?: (e: DragEvent<HTMLElement>) => void
  onDrop?: (e: DragEvent<HTMLElement>) => void
}

export interface Reorderable {
  /** ``ids`` in the order the user chose. */
  orderedIds: string[]
  /** Spread on each item. */
  getItemProps: (id: string) => ReorderItemProps
  /** Spread on the element that wraps the items. */
  containerProps: ReorderContainerProps
}

const DRAG_TYPE = 'application/x-prism-reorder'

function readOrder(key: string | undefined): string[] | null {
  if (key === undefined) return null
  try {
    return parseStoredOrder(window.localStorage.getItem(key))
  } catch {
    return null
  }
}

/**
 * Drag-to-reorder for a list of items, with the order saved in localStorage
 * under ``storageKey`` (read once per mount; synced across tabs). Without a
 * key it's inert: the default order and no drag handlers.
 *
 * Uses native HTML drag and drop. The drop goes where the insertion line
 * shows, even if the pointer is released over the gap between two items.
 */
export function useReorderable(
  storageKey: string | undefined,
  ids: readonly string[],
  axis: ReorderAxis,
): Reorderable {
  // Read during the first render, not in an effect like ``useLocalStorage``:
  // the saved order has to be there on the first paint, or the items jump
  // on every page load.
  const [stored, setStored] = useState(() => readOrder(storageKey))
  const [dragging, setDragging] = useState<string | null>(null)
  const [drop, setDrop] = useState<{ id: string; position: DropPosition } | null>(null)
  // The drag itself rides on refs: drop can fire before React re-renders
  // from the last dragover. The state copies only drive the styling.
  const draggingRef = useRef<string | null>(null)
  const dropRef = useRef<{ id: string; position: DropPosition } | null>(null)

  useEffect(() => {
    if (storageKey === undefined) return
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) setStored(parseStoredOrder(e.newValue))
    }
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('storage', onStorage)
    }
  }, [storageKey])

  const orderedIds = applyStoredOrder(ids, stored)

  if (storageKey === undefined) {
    return { orderedIds, getItemProps: () => ({}), containerProps: {} }
  }

  const setDropTarget = (next: { id: string; position: DropPosition } | null) => {
    const prev = dropRef.current
    if (prev?.id === next?.id && prev?.position === next?.position) return
    dropRef.current = next
    setDrop(next)
  }

  const reset = () => {
    draggingRef.current = null
    dropRef.current = null
    setDragging(null)
    setDrop(null)
  }

  const getItemProps = (id: string): ReorderItemProps => ({
    draggable: true,
    'data-reorder-id': id,
    'data-dragging': dragging === id || undefined,
    'data-drop': drop?.id === id ? drop.position : undefined,
    onDragStart: (e) => {
      e.dataTransfer.effectAllowed = 'move'
      // Firefox only starts a drag that carries data.
      e.dataTransfer.setData(DRAG_TYPE, id)
      draggingRef.current = id
      // Dim it on the next frame: the browser snapshots the drag image right
      // after this handler, and the image should show the item undimmed.
      requestAnimationFrame(() => {
        if (draggingRef.current === id) setDragging(id)
      })
    },
    onDragEnd: reset,
  })

  const containerProps: ReorderContainerProps = {
    onDragOver: (e) => {
      const from = draggingRef.current
      // A drag that didn't start in this list (a file, a tab from another
      // list). The type check also covers a dragend the browser never fired,
      // which would leave the ref set.
      if (from === null || !e.dataTransfer.types.includes(DRAG_TYPE)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      const item =
        e.target instanceof Element ? e.target.closest<HTMLElement>('[data-reorder-id]') : null
      // Over the gap between items: keep the last line where it is.
      if (!item || !e.currentTarget.contains(item)) return
      const target = item.dataset['reorderId']
      if (target === undefined) return
      const rect = item.getBoundingClientRect()
      const before =
        axis === 'x'
          ? e.clientX < rect.left + rect.width / 2
          : e.clientY < rect.top + rect.height / 2
      const position: DropPosition = before ? 'before' : 'after'
      // No line where dropping changes nothing: on the item itself, or on
      // the side of a neighbour it already sits on.
      const noop = sameOrder(moveItem(orderedIds, from, target, position), orderedIds)
      setDropTarget(noop ? null : { id: target, position })
    },
    onDrop: (e) => {
      const from = draggingRef.current
      if (from === null) return
      e.preventDefault()
      const target = dropRef.current
      if (target) {
        const next = moveItem(orderedIds, from, target.id, target.position)
        setStored(next)
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next))
        } catch {
          /* quota / access denied — the order still holds until reload */
        }
      }
      reset()
    },
  }

  return { orderedIds, getItemProps, containerProps }
}
